import React from "react";
import { withTranslation } from "react-i18next";
import Buildings from "../components/buildings";
import { Face1, Face2, Face3 } from "../components/animations/instructions";
import Privacy from "./privacy.register";
import UploadImage from "./uploadImage";
import Slide from "react-reveal/Slide";
import { GetUrlVars, ResetUrl, isLocalhost } from "../services/data.service";
import { Full as Loader } from "../components/loaders/full";
import { Config } from "../app.config";
import RegistrationDetails from "./registrationDetails";
import VerifyRegistration from "./verifyRegistration";
import Welcome from "./Welcome";

class UnknownPerson extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            msg: "",
            hash: "",
            code: "",
            phone: "",
            email: "",
            name: "",
            photo: "",
            enrollmentWelcomeHeadline: "",
            enrollmentWelcomeContent: "",
            enrollmentConsentHeadline: "",
            enrollmentConsentContent: "",
            learnMore: "",
            steps: [],
            current: null,
            loader: false
        };

        this.on = {
            click: (item, isDirty) => {
                if (isDirty && !item.dirty) return;
                const steps = [...this.state.steps];
                steps.forEach(el => {
                    el.active = el.id === item.id;
                    if (el.active) {
                        el.dirty = true;
                    }
                });
                this.setState({ current: item, steps: steps }, () => {
                    const element = document.getElementById("stepper-facelabs");
                    if (element) {
                        element.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
                    }
                });
            }
        };
    }

    componentDidMount() {
        const { i18n } = this.props; // Get i18n instance
        const params = GetUrlVars();
        const isValid = params.hash && params.code;
        const queryLanguage = params.lang; // Check for ?lang= in URL

        const _params = {
            hash: params.hash,
            code: params.code,
        };

        // SMS is enabled for Israeli numbers (+972) — decided per phone number in RegistrationDetails
        if (params.contact_mail) window.contact_mail = params.contact_mail;
        if (params.address) window.address = params.address;

        if (isValid) {
            this.setState({
                hash: _params.hash,
                code: _params.code,
                phone: "",
            });
        }

        if (params.site) {
            fetch(`${Config.routes.siteDetails}?site=${params.site}`, {
                method: "get",
                headers: { "Content-Type": "application/json" }
            })
                .then(res => res.json())
                .then(result => {
                    if (result) {
                        const finalLanguage = queryLanguage || result.enrollment_language || "en";

                        // Ensure i18next updates the language
                        if (finalLanguage !== i18n.language) {
                            i18n.changeLanguage(finalLanguage);
                            // Set document language and direction for RTL support
                            document.documentElement.lang = finalLanguage;
                            document.documentElement.dir = finalLanguage === "he" ? "rtl" : "ltr";
                        }

                        this.setState({
                            enrollment_language: finalLanguage,
                            enrollmentWelcomeHeadline: result.enrollment_welcome_headline,
                            enrollmentWelcomeContent: result.enrollment_welcome_content,
                            enrollmentConsentHeadline: result.enrollment_consent_headline,
                            enrollmentConsentContent: result.enrollment_consent_content,
                            learnMore: result.enrollment_learn_more
                        }, this.setSteps);
                    } else {
                        this.setSteps();
                    }
                })
                .catch(error => {
                    console.error("Error fetching site details:", error);
                    this.setSteps();
                });
        } else {
            // If no API call, still check for query param
            const finalLanguage = queryLanguage || "en";

            if (finalLanguage !== i18n.language) {
                i18n.changeLanguage(finalLanguage);
                // Set document language and direction for RTL support
                document.documentElement.lang = finalLanguage;
                document.documentElement.dir = finalLanguage === "he" ? "rtl" : "ltr";
            }

            this.setState({
                enrollment_language: finalLanguage,
            }, this.setSteps);
        }

        if (!isLocalhost()) ResetUrl("/register");
    }

    setSteps = () => {
        const { t } = this.props;
        const learnMoreSite = this.state.learnMore || "https://proptech.ai";

        const welcome = {
            id: 0,
            component: <Welcome title={this.state.enrollmentWelcomeHeadline} content={this.state.enrollmentWelcomeContent} />,
            text: "",
            btnTxt: t("btn_lets_go"),
            onClick: () => this.on.click(this.state.steps[1])
        };
        const privacy = {
            id: 1,
            dirty: true,
            component: <Privacy title={this.state.enrollmentConsentHeadline} content={this.state.enrollmentConsentContent} />,
            btnTxt: t("btn_agree"),
            onClick: () => this.on.click(this.state.steps[2]),
            active: true
        };
        const details = {
            id: 2,
            component: <RegistrationDetails onChange={(name, email, phone) => {
                this.setState({
                    phone: phone,
                    email: email,
                    name: name,
                });
                // Israeli numbers go through SMS verification (step 3); others skip it (step 4)
                const nextStep = (phone && phone.startsWith('+972')) ? this.state.steps[3] : this.state.steps[4];
                this.on.click(nextStep);
            }} />,
            onClick: () => {}
        };
        const verify = {
            id: 3,
            component: <VerifyRegistration currentphone={() => this.state.phone} onChange={(phone) => {
                this.setState({
                    phone: phone,
                });
                this.on.click(this.state.steps[4]);
            }} />,
            onClick: () => {}
        };
        const thanks = {
            id: 7,
            component: <Face3 />,
            text: (
                <div className="flex flex-col text-center w-full mt-20">
                    <h1 className="sm:text-3xl text-2xl font-medium title-font text-gray-900">{t("thank_you")}</h1>
                    <h2 className="text-xs text-blue-900 tracking-widest font-medium title-font">
                        {t("registration_completed")}
                    </h2>
                    <h2 className="text-xs text-blue-900 tracking-widest font-medium title-font">
                        <a style={{color: "inherit"}} href={learnMoreSite}>{t("learn_more", {site: learnMoreSite})}</a>
                    </h2>
                </div>
            )
        };

        this.setState({
            steps: [welcome, privacy, details, verify,
                {id: 4, component: <Face1/>, btnTxt: t("btn_next"), onClick: () => this.on.click(this.state.steps[5]) },
                { id: 5, component: <Face2 />, btnTxt: t("btn_next"), onClick: () => this.on.click(this.state.steps[6]) },
                {
                    id: 6, component: <UploadImage onChange={(photo) => this.setState({ photo })} onSubmit={this.sendForm} />,
                    onClick: () => {}
                },
                thanks
            ],
            current: welcome
        });
    }

    sendForm = () => {
        const { hash, code, photo, phone, email, name } = this.state;
        const isValid = [hash, code, photo].filter(v => !v).length === 0;

        if (isValid) {
            this.setState({ loader: true });
            fetch(Config.routes.selfRegister, {
                method: "post",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ hash, code, phone, email, name, photo })
            }).then(res => {
                this.setState({ loader: false });
                if (res.status === 200) {
                    this.on.click(this.state.steps[7]);
                    this.setState({ current: this.state.steps[7], msg: "" });
                } else {
                    return res.json();
                }
            }).then(result => {
                if (result && typeof result.msg === "string") {
                    this.setState({ msg: result.msg });
                }
            });
        }
    }

    render() {
        const { t } = this.props;
        return (
            <main className="bg-white font-open-sans">
                <section className="text-gray-700 body-font">
                    <div className="text-center mx-auto px-6 sm:px-12 flex flex-col-reverse sm:flex-row relative">
                        <div className="sm:w-12/12 xl:w-12/12 flex flex-col items-center sm:text-center mx-auto relative z-10">
                            {this.state.current?.text && <div>{this.state.current.text}</div>}
                            <div className="mt-5">{this.state.current?.component}</div>
                            <Slide bottom>
                                <div>
                                    {this.state.current?.btnTxt &&
                                        <button onClick={this.state.current.onClick} className="flashing text-white bg-blue-900 py-2 px-6 hover:bg-blue-600 rounded text-lg">
                                            {this.state.current.btnTxt}
                                        </button>}
                                </div>
                            </Slide>
                            {this.state.msg ? <h1 style={{ color: 'red' }} className="sm:text-3xl text-2xl font-medium title-font mb-2 text-gray-900">{t("error")}:{this.state.msg}</h1> : null}
                        </div>
                        <Buildings />
                    </div>
                </section>
                {this.state.loader && <Loader />}
            </main>
        );
    }
}

export default withTranslation()(UnknownPerson);
